import { autoTranslate } from "@web-monorepo/translation-runtime";
import { TranslatedString } from "@web-monorepo/i18next";

export const localeToString: Record<string, string> = {
  "en-US": "English (US)",
  "en-GB": "English (UK)",
  "id-ID": "Bahasa Indonesia",
  "bn-BD": "বাংলা",
  "ca-ES": "Català",
  "de-DE": "Deutsch",
  "es-MX": "Español",
  "es-ES": "Español (España)",
  "fa-IR": "فارسی",
  "fr-CA": "Français (Canada)",
  "fr-FR": "Français (France)",
  "hr-HR": "Hrvatski",
  "it-IT": "Italiano",
  "ht-HT": "Kreyòl Ayisyen",
  "lt-LT": "Lietuvių",
  "nl-NL": "Nederlands",
  "pl-PL": "Polski",
  "pt-BR": "Português (Brasil)",
  "pt-PT": "Português (Portugal)",
  "ro-RO": "Română",
  "sk-SK": "Slovenčina",
  "sl-SI": "Slovenščina",
  "tr-TR": "Türkçe",
  "vi-VN": "Tiếng Việt",
  "be-BY": "Беларуская",
  "el-GR": "Ελληνικά",
  "bg-BG": "Български",
  "ru-RU": "Русский",
  "sr-RS": "Srpski",
  "uk-UA": "Українська",
  "he-IL": "עברית",
  "ar-AR": "العربية",
  "hi-IN": "हिन्दी",
  "bn-IN": "বাংলা",
  "pa-IN": "ਪੰਜਾਬੀ",
  "ko-KR": "한국어",
  "zh-TW": "繁體中文",
  "zh-CN": "简体中文",
  "ja-JP": "日本語",
  "th-TH": "ไทย",
  "fil-PH": "Pilipino",
  "ms-MY": "Bahasa Melayu",
  "ur-IN": "اردو (بھارت)",
  "ps-AC": "پښتو",
  "ps-BD": "پښتو",
  "ps-KY": "پښتو",
};

const SUPPORTED_SPANISH = ["es-MX", "es-ES"];

export function mapLocaleToI18nLocale(locale: string) {
  if (locale === "en-US") {
    // en-US and en are actually the same, because the source of the translations ("en")
    // is written by people in the US. Rewrite requests from "en-US" to "en" so we don't
    // fetch two bundles with the same data. Also because test environments only know how
    // to serve "en".
    return "en";
  }

  if ((locale === "es" || locale.startsWith("es-")) && !SUPPORTED_SPANISH.includes(locale)) {
    return SUPPORTED_SPANISH[0];
  }

  return locale;
}

export function getTranslatedLanguageName(language: string | undefined | null): TranslatedString | null {
  if (!language) return null;
  return (
    {
      af: autoTranslate("Afrikaans"),
      "af-ZA": autoTranslate("Afrikaans"),
      am: autoTranslate("Amharic"),
      "am-ET": autoTranslate("Amharic"),
      ar: autoTranslate("Arabic"),
      "ar-AR": autoTranslate("Arabic"),
      be: autoTranslate("Belarusian"),
      "be-BY": autoTranslate("Belarusian"),
      bg: autoTranslate("Bulgarian"),
      "bg-BG": autoTranslate("Bulgarian"),
      bn: autoTranslate("Bengali"),
      "bn-BD": autoTranslate("Bengali"),
      ca: autoTranslate("Catalan"),
      "ca-ES": autoTranslate("Catalan"),
      cs: autoTranslate("Czech"),
      "cs-CZ": autoTranslate("Czech"),
      cy: autoTranslate("Welsh"),
      "cy-GB": autoTranslate("Welsh"),
      da: autoTranslate("Danish"),
      "da-DK": autoTranslate("Danish"),
      de: autoTranslate("German"),
      "de-DE": autoTranslate("German"),
      el: autoTranslate("Greek"),
      "el-GR": autoTranslate("Greek"),
      en: autoTranslate("English"),
      "en-GB": autoTranslate("English (UK)"),
      "en-US": autoTranslate("English (US)"),
      es: autoTranslate("Spanish"),
      "es-ES": autoTranslate("Spanish (Spain)"),
      "es-MX": autoTranslate("Spanish (Latin America)"),
      et: autoTranslate("Estonian"),
      "et-EE": autoTranslate("Estonian"),
      eu: autoTranslate("Basque"),
      "eu-ES": autoTranslate("Basque"),
      fa: autoTranslate("Persian"),
      "fa-IR": autoTranslate("Persian"),
      fi: autoTranslate("Finnish"),
      "fi-FI": autoTranslate("Finnish"),
      fil: autoTranslate("Filipino"),
      "fil-PH": autoTranslate("Filipino (Philippines)"),
      fr: autoTranslate("French"),
      "fr-CA": autoTranslate("French (Canada)"),
      "fr-FR": autoTranslate("French (France)"),
      ga: autoTranslate("Gaelic"),
      "ga-IE": autoTranslate("Gaelic"),
      gl: autoTranslate("Galician"),
      "gl-ES": autoTranslate("Galician"),
      he: autoTranslate("Hebrew"),
      "he-IL": autoTranslate("Hebrew"),
      ht: autoTranslate("Haitian Creole"),
      "ht-HT": autoTranslate("Haitian Creole"),
      hi: autoTranslate("Hindi"),
      "hi-IN": autoTranslate("Hindi"),
      hr: autoTranslate("Croatian"),
      "hr-HR": autoTranslate("Croatian"),
      hu: autoTranslate("Hungarian"),
      "hu-HU": autoTranslate("Hungarian"),
      id: autoTranslate("Indonesian"),
      "id-ID": autoTranslate("Indonesian"),
      it: autoTranslate("Italian"),
      "it-IT": autoTranslate("Italian"),
      ja: autoTranslate("Japanese"),
      "ja-JP": autoTranslate("Japanese"),
      kk: autoTranslate("Kazakh"),
      "kk-KZ": autoTranslate("Kazakh"),
      ko: autoTranslate("Korean"),
      "ko-KR": autoTranslate("Korean"),
      lt: autoTranslate("Lithuanian"),
      "lt-LT": autoTranslate("Lithuanian"),
      lv: autoTranslate("Latvian"),
      "lv-LV": autoTranslate("Latvian"),
      ml: autoTranslate("Malayalam"),
      "ml-IN": autoTranslate("Malayalam"),
      ms: autoTranslate("Malay"),
      "ms-MY": autoTranslate("Malay"),
      nb: autoTranslate("Norwegian"),
      "nb-NO": autoTranslate("Norwegian"),
      nl: autoTranslate("Dutch"),
      "nl-NL": autoTranslate("Dutch"),
      pa: autoTranslate("Punjabi"),
      "pa-IN": autoTranslate("Punjabi (India)"),
      "pa-PK": autoTranslate("Punjabi (Pakistan)"),
      pl: autoTranslate("Polish"),
      "pl-PL": autoTranslate("Polish"),
      pt: autoTranslate("Portuguese"),
      "pt-BR": autoTranslate("Portuguese (Brazil)"),
      "pt-PT": autoTranslate("Portuguese (Portugal)"),
      ro: autoTranslate("Romanian"),
      "ro-RO": autoTranslate("Romanian"),
      ru: autoTranslate("Russian"),
      "ru-RU": autoTranslate("Russian"),
      sk: autoTranslate("Slovakian"),
      "sk-SK": autoTranslate("Slovakian"),
      sl: autoTranslate("Slovenian"),
      "sl-SI": autoTranslate("Slovenian"),
      sr: autoTranslate("Serbian"),
      "sr-SP": autoTranslate("Serbian"),
      sv: autoTranslate("Swedish"),
      "sv-SE": autoTranslate("Swedish"),
      sw: autoTranslate("Swahili"),
      "sw-KE": autoTranslate("Swahili"),
      ta: autoTranslate("Tamil"),
      "ta-IN": autoTranslate("Tamil"),
      te: autoTranslate("Telugu"),
      "te-IN": autoTranslate("Telugu"),
      th: autoTranslate("Thai"),
      "th-TH": autoTranslate("Thai"),
      tr: autoTranslate("Turkish"),
      "tr-TR": autoTranslate("Turkish"),
      uk: autoTranslate("Ukrainian"),
      "uk-UA": autoTranslate("Ukrainian"),
      ur: autoTranslate("Urdu"),
      "ur-IN": autoTranslate("Urdu"),
      "ur-PK": autoTranslate("Urdu"),
      vi: autoTranslate("Vietnamese"),
      "vi-VN": autoTranslate("Vietnamese"),
      zh: autoTranslate("Chinese"),
      "zh-CN": autoTranslate("Chinese (Simplified)"),
      "zh-TW": autoTranslate("Chinese (Traditional)"),
      zu: autoTranslate("Zulu"),
      "zu-ZA": autoTranslate("Zulu"),
    }[language] ??
    (language as TranslatedString) ??
    null
  );
}

/**
 * Languages a reader can have content auto-translated into, labelled in their own
 * script. Distinct from {@link localeToString}, which is the much smaller set of
 * locales ClassDojo ships a translated UI for.
 *
 * Key order is hand-maintained and only roughly alphabetical — `tl` ("Tagalog") sits
 * between Esperanto and Français. Never render this map directly; use
 * {@link translationLanguages}.
 */
export const CONTENT_TRANSLATION_LANGUAGE: Record<string, string> = {
  om: "Afaan Oromoo",
  af: "Afrikaans",
  ak: "Akan",
  gn: "Avañe'ẽ",
  ay: "Aymar aru",
  az: "Azərbaycan dili",
  id: "Bahasa Indonesia",
  ms: "Bahasa Melayu",
  bm: "Bamanankan",
  jv: "Basa Jawa",
  su: "Basa Sunda",
  bs: "Bosanski",
  ca: "Català",
  cs: "Čeština",
  ny: "Chi-Chewa",
  sn: "ChiShona",
  co: "Corsu",
  da: "Dansk",
  de: "Deutsch",
  et: "Eesti",
  en: "English",
  es: "Español",
  eo: "Esperanto",
  eu: "Euskara",
  ee: "Eʋegbe",
  fil: "Filipino",
  fr: "Français",
  fy: "Frysk",
  ga: "Gaeilge",
  sm: "Gagana Samoa",
  gd: "Gàidhlig",
  gl: "Galego",
  hmn: "Hmoob",
  hr: "Hrvatski",
  haw: "ʻŌlelo Hawaiʻi",
  ig: "Igbo",
  ilo: "Ilokano",
  is: "Íslenska",
  it: "Italiano",
  rw: "Kinyarwanda",
  sw: "Kiswahili",
  ht: "Kreyòl Ayisyen",
  kri: "Krio",
  ku: "Kurdî",
  la: "Latina",
  lv: "Latviešu",
  lb: "Lëtzebuergesch",
  lt: "Lietuvių",
  ln: "Lingála",
  lg: "Luganda",
  hu: "Magyar",
  mg: "Malagasy",
  mt: "Malti",
  mi: "Māori",
  lus: "Mizo ţawng",
  nl: "Nederlands",
  no: "Norsk",
  pl: "Polski",
  pt: "Português",
  ro: "Română",
  qu: "Runa Simi",
  st: "Sesotho",
  nso: "Sesotho sa Leboa",
  sq: "Shqip",
  ceb: "Sinugboanong Binisaya",
  sk: "Slovenčina",
  sl: "Slovenščina",
  so: "Soomaali",
  fi: "Suomi",
  sv: "Svenska",
  tl: "Tagalog",
  tr: "Türkçe",
  tk: "Türkmençe",
  ur: "Urdu",
  ug: "Uyghur",
  uz: "Uzbek",
  vi: "Vietnamese",
  cy: "Welsh",
  xh: "Xhosa",
  ts: "Xitsonga",
  yi: "Yiddish",
  yo: "Yoruba",
  zu: "Zulu",
  el: "Ελληνικά",
  be: "Беларуская",
  bg: "Български",
  ky: "Кыргызча",
  kk: "Қазақ тілі",
  mk: "Македонски",
  mn: "Монгол",
  ru: "Русский",
  sr: "Српски",
  tt: "татарча",
  tg: "тоҷикӣ",
  uk: "Українська",
  ka: "ქართული",
  hy: "Հայերեն",
  he: "עברית",
  ar: "العربية",
  ps: "پښتو",
  sd: "سنڌي",
  fa: "فارسی",
  ckb: "کوردیی ناوەندی",
  ha: "هَوُسَ",
  dv: "ދިވެހި",
  ti: "ትግርኛ",
  am: "አማርኛ",
  gom: "कोंकणी",
  doi: "डोगरी",
  ne: "नेपाली",
  bho: "भोजपुरी",
  mr: "मराठी",
  mai: "मैथिली",
  sa: "संस्कृतम्",
  hi: "हिन्दी",
  as: "অসমীয়া",
  bn: "বাংলা",
  pa: "ਪੰਜਾਬੀ",
  gu: "ગુજરાતી",
  or: "ଓଡ଼ିଆ",
  ta: "தமிழ்",
  te: "తెలుగు",
  kn: "ಕನ್ನಡ",
  ml: "മലയാളം",
  si: "සිංහල",
  "mni-Mtei": "ꯃꯤꯇꯩ ꯃꯌꯥꯏ",
  th: "ไทย",
  lo: "ລາວ",
  my: "မြန်မာစာ",
  km: "ភាសាខ្មែរ",
  ko: "한국어",
  "zh-CN": "中文 (简体)",
  "zh-TW": "中文 (繁體)",
  ja: "日本語",
};

export type TranslationLanguageOption = { value: string; label: string };

/**
 * The canonical option list for every translation-language picker, sorted by the label
 * the reader actually sees. The collation locale is pinned so the order can't vary with
 * the runtime's default locale.
 */
export const translationLanguages: TranslationLanguageOption[] = Object.entries(CONTENT_TRANSLATION_LANGUAGE)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label, "en", { sensitivity: "base" }));

/**
 * Display name for a translation-language code, in the reader's UI language when ClassDojo has
 * one ("Spanish"), otherwise the language's own name from the translation list ("Afaan Oromoo").
 * {@link getTranslatedLanguageName} covers about half of the translation codes and echoes the raw
 * code back for the rest, which would read as "messages in om".
 */
export function translationLanguageDisplayName(code: string): TranslatedString {
  const translated = getTranslatedLanguageName(code);
  if (translated && translated !== code) return translated;
  const endonym = CONTENT_TRANSLATION_LANGUAGE[code];
  // Endonyms are already in their own language, so they are safe to show untranslated.
  if (endonym) return endonym as TranslatedString;
  // getTranslatedLanguageName already echoes an unknown code back as its own name. It only returns
  // null for an empty code, and an unknown code has no name in any language to show instead.
  return translated ?? (code as TranslatedString);
}
